import { useState } from "react";
import { NavLink } from "react-router-dom";
import { Tooltip } from "primereact/tooltip";

import LogoFull from "@/assets/icons/logo-hub-full.svg?react";
import LogoCompact from "@/assets/icons/logo-hub-compact.svg?react";
import logoHubFullUrl from "@/assets/icons/logo-hub-full.svg";
import { layoutStore, useLayoutStore } from "@/stores/layout-store";
import { UserMenu } from "../user-menu";
import type { MenuItem, MenuItemIcon } from "./sidebar.types";
import styles from "./sidebar.module.scss";

interface SidebarProps {
  items: MenuItem[];
  secondaryItems?: MenuItem[];
}

/** Aceita tanto a classe de um `pi pi-*` quanto um SVG do projeto. */
function NavIcon({ icon }: { icon: MenuItemIcon }) {
  if (typeof icon === "string") return <i className={`${icon} ${styles.navIcon}`} aria-hidden />;

  const Icon = icon;
  return <Icon className={styles.navIcon} aria-hidden focusable="false" />;
}

export function Sidebar({ items, secondaryItems }: SidebarProps) {
  const { menuIsOpen } = useLayoutStore();
  const collapsed = !menuIsOpen;
  const [mobileOpen, setMobileOpen] = useState(false);

  function toggleMenu() {
    if (mobileOpen) {
      setMobileOpen(false);
      return;
    }
    layoutStore.set({ menuIsOpen: collapsed });
  }

  function closeMobile() {
    setMobileOpen(false);
  }

  return (
    <>
      <div className={styles.mobileBar}>
        <button
          type="button"
          className={styles.mobileToggle}
          onClick={() => setMobileOpen(true)}
          aria-label="Abrir menu"
          aria-expanded={mobileOpen}
          data-testid="sidebar-mobile-toggle"
        >
          <i className="pi pi-bars" aria-hidden />
        </button>
        <img src={logoHubFullUrl} className={styles.mobileBarLogo} alt="Hub de Obras" />

        <div className={styles.mobileBarUser}>
          <UserMenu collapsed openBelow triggerTestId="sidebar-mobile-user-button" />
        </div>
      </div>

      {mobileOpen ? (
        <div
          className={styles.backdrop}
          onClick={closeMobile}
          aria-hidden
          data-testid="sidebar-backdrop"
        />
      ) : null}

      <aside
        className={[
          styles.sidebar,
          collapsed ? styles.sidebarCollapsed : "",
          mobileOpen ? styles.sidebarMobileOpen : "",
        ]
          .filter(Boolean)
          .join(" ")}
        data-testid="app-sidebar"
        data-state={collapsed ? "collapsed" : "expanded"}
      >
        <div className={styles.top}>
          <div className={styles.brand}>
            <LogoFull className={styles.brandLogoFull} role="img" aria-label="Hub de Obras" />
            <LogoCompact className={styles.brandLogoCompact} role="img" aria-label="Hub de Obras" />
          </div>

          <nav aria-label="Menu principal">
            <ul className={styles.navList}>
              {items.map(({ label, icon, path, testId, end }) => (
                <li key={path}>
                  <NavLink
                    to={path}
                    end={end ?? true}
                    onClick={closeMobile}
                    className={({ isActive }) =>
                      [styles.navLink, isActive ? styles.navLinkActive : ""]
                        .filter(Boolean)
                        .join(" ")
                    }
                    data-testid={testId}
                    {...(collapsed ? { "data-pr-tooltip": label } : {})}
                  >
                    <NavIcon icon={icon} />
                    <span
                      className={`${styles.navLabel} ${collapsed ? styles.navLabelCollapsed : ""}`}
                    >
                      {label}
                    </span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>

          {secondaryItems?.length ? (
            <>
              <hr className={styles.navDivider} aria-hidden />
              <nav aria-label="Menu secundário">
                <ul className={styles.navList}>
                  {secondaryItems.map(({ label, icon, path, testId, end }) => (
                    <li key={path}>
                      <NavLink
                        to={path}
                        end={end ?? true}
                        onClick={closeMobile}
                        className={({ isActive }) =>
                          [styles.navLink, isActive ? styles.navLinkActive : ""]
                            .filter(Boolean)
                            .join(" ")
                        }
                        data-testid={testId}
                        {...(collapsed ? { "data-pr-tooltip": label } : {})}
                      >
                        <NavIcon icon={icon} />
                        <span
                          className={`${styles.navLabel} ${collapsed ? styles.navLabelCollapsed : ""}`}
                        >
                          {label}
                        </span>
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </nav>
            </>
          ) : null}
        </div>

        <div className={styles.footer}>
          <UserMenu collapsed={mobileOpen ? false : collapsed} />
        </div>

        <button
          type="button"
          className={styles.toggle}
          onClick={toggleMenu}
          aria-label={collapsed ? "Expandir menu" : "Recolher menu"}
          aria-expanded={menuIsOpen}
          data-testid="sidebar-toggle"
        >
          <i className={`pi ${collapsed ? "pi-chevron-right" : "pi-chevron-left"}`} aria-hidden />
        </button>

        {collapsed ? (
          <Tooltip
            target={`.${styles.navLink}`}
            position="right"
            className={styles.tooltip}
            event="both"
          />
        ) : null}
      </aside>
    </>
  );
}
